import { useEffect, useState, useCallback, useRef } from 'react';
import { configLoader } from '@/lib/config-loader';
import { useLearnerState } from '@/app/hooks/useLearnerState';
import { useModuleConfig } from '@/app/hooks/useModuleConfig';
import { SlideLayoutRouter } from './layouts/SlideLayoutRouter';
import { SlideTransition } from './SlideTransition';
import { NotepadBar } from '../notepad/NotepadBar';
import type { LearnerState, SlideConfig } from '@/lib/types';

interface SlideViewerProps {
  moduleId: string;
  state: LearnerState;
}

export function SlideViewer({ moduleId, state }: SlideViewerProps) {
  const [slides, setSlides] = useState<SlideConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const { updatePosition, updateNotepad, saveNotepad } = useLearnerState();
  const { config: moduleConfig } = useModuleConfig(moduleId);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const currentPartIndex = state.currentPosition.partIndex;
  const currentSlideIndex = state.currentPosition.slideIndex;
  const partId = `part-${currentPartIndex + 1}`;

  // Get current part's accent color from module config
  const currentPart = moduleConfig?.parts[currentPartIndex];
  const accentColor = currentPart?.accentColor || '#4A6741';

  useEffect(() => {
    async function loadSlides() {
      try {
        setLoading(true);
        const slidesConfig = await configLoader.loadPartSlides(moduleId, partId);
        setSlides(slidesConfig.slides);
      } catch (error) {
        console.error('Failed to load slides:', error);
      } finally {
        setLoading(false);
      }
    }

    loadSlides();
  }, [moduleId, currentPartIndex, partId]);

  // Find notepad config from slides (check the first/title slide)
  const notepadConfig = slides.find((s) => s.notepad)?.notepad;

  // Get current notepad value from state
  const notepadValue = state.parts[partId]?.notepad?.answer || '';

  // Debounced save — persist to backend 1s after typing stops
  const handleNotepadChange = useCallback(
    (value: string) => {
      if (!notepadConfig) return;
      updateNotepad(partId, notepadConfig.prompt, value);

      // Debounce backend save
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        saveNotepad(partId);
      }, 1000);
    },
    [partId, notepadConfig, updateNotepad, saveNotepad]
  );

  // Save notepad on phase change
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      if (notepadConfig && notepadValue) {
        saveNotepad(partId);
      }
    };
  }, [currentPartIndex]);

  const currentSlide = slides[currentSlideIndex];

  const handleNext = () => {
    if (currentSlideIndex < slides.length - 1) {
      updatePosition({
        ...state.currentPosition,
        slideIndex: currentSlideIndex + 1,
      });
    } else {
      // Move to assessment phase
      updatePosition({
        ...state.currentPosition,
        phase: 'assessment',
        slideIndex: 0,
      });
    }

    // Smoothly scroll to top of new slide after transition starts
    requestAnimationFrame(() => {
      const scrollContainer = document.querySelector('.overflow-auto');
      if (scrollContainer) {
        scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  };

  const handlePrevious = () => {
    if (currentSlideIndex > 0) {
      updatePosition({
        ...state.currentPosition,
        slideIndex: currentSlideIndex - 1,
      });
    }

    // Smoothly scroll to top of new slide after transition starts
    requestAnimationFrame(() => {
      const scrollContainer = document.querySelector('.overflow-auto');
      if (scrollContainer) {
        scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!currentSlide) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-600 dark:text-slate-400">No slides available</p>
      </div>
    );
  }

  return (
    <div className={`slide-container ${notepadConfig ? 'pb-20' : ''}`}>
      {/* Slide content with layout router and transitions */}
      <div
        className="mb-8"
        style={{ '--color-accent': accentColor } as React.CSSProperties}
      >
        <SlideTransition slideKey={currentSlideIndex}>
          <SlideLayoutRouter slide={currentSlide} accentColor={accentColor} />
        </SlideTransition>
      </div>

      {/* Navigation */}
      <div
        className="max-w-4xl mx-auto"
        style={{ '--color-accent': accentColor } as React.CSSProperties}
      >
        {/* Progress bar */}
        <div className="mb-6">
          <div className="w-full h-1 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full accent-bg rounded-full transition-all duration-500 ease-out"
              style={{ width: `${((currentSlideIndex + 1) / slides.length) * 100}%` }}
            />
          </div>
          <div className="flex justify-between mt-2">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {currentSlideIndex + 1} of {slides.length}
            </span>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              Part {currentPartIndex + 1}
            </span>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex items-center justify-between">
          <button
            onClick={handlePrevious}
            disabled={currentSlideIndex === 0}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
            Previous
          </button>

          <button
            onClick={handleNext}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-all active:scale-[0.98] accent-bg hover:opacity-90 text-white"
          >
            {currentSlideIndex < slides.length - 1 ? 'Next' : 'Continue to Assessment'}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </div>
      </div>

      {/* Notepad bar (persistent at bottom) */}
      {notepadConfig && (
        <NotepadBar
          prompt={notepadConfig.prompt}
          value={notepadValue}
          onChange={handleNotepadChange}
          accentColor={accentColor}
        />
      )}
    </div>
  );
}
